import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';

import { useParams, useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { useInView } from 'react-intersection-observer';
import { Helmet } from 'react-helmet';
import Flag from 'react-world-flags';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

import Text from 'common/components/Text/Text';
import { ExploreState } from 'features/explore/exploreSlice';
import { TabsComponent } from 'features/explore/school/tabs/Tabs';
import Loader from 'common/components/Loader/Loader';
import StudentLife from 'features/explore/common/components/StudentLifeSection';
import SimilarSchools from 'features/explore/school/tabs/SimilarSchoolsTab';

import { useGetSchoolQuery } from 'services/exploreApi';
import { newDetailUrl } from 'common/utils/conversions';
import { useAppDispatch, useAppSelector } from 'app/hooks';

import styles from './SchoolDetail.module.scss';
import textStyles from 'common/styles/text.module.scss';

import { setFromExplore } from 'features/explore/exploreSlice';
import Placeholder from 'assets/explore/placeholder.png';
import { Discount } from 'app/types';
import { OverviewComponent } from '../common/components/Overview';
import { navigateTop } from 'common/utils';
import { MatchingSection } from '../common/components/MatchingSection';
import { TagsSection } from '../common/components/TagsSection';
import { StudySection } from '../common/components/StudySection';
import { ContactBanner } from '../common/components/ContactBanner';
import { ProfilingBanner } from '../common/components/ProfilingBanner';
import { TeachingSection } from '../common/components/TeachingSection';
import { FAQSection } from 'features/explore/common/components/FaqSection';
import { ApplySection, JourneyBanner } from '../common/components/ApplySection';
import { CareerSection } from '../common/components/CareerSection';
import { VisaSection } from '../common/components/VisaSection';
import { DetailSections } from '../common/components/DetailSections';
import { SessionState } from 'features/session/sessionSlice';
import { InternationalStudentsComponent } from '../common/components/InternationalStudentSection';

const SchoolDetail: FunctionComponent = () => {
  const { schoolSlug } = useParams<{ schoolSlug: string }>();
  const [schoolId] = schoolSlug?.split('--') || [];
  const {
    data: school,
    isLoading,
    isFetching,
  } = useGetSchoolQuery({ id: parseInt(schoolId || ''), view: 'detailed' });
  const { comesFromExplore } = useAppSelector(({ explore }: { explore: ExploreState }) => explore);
  const { logged } = useAppSelector(({ session }: { session: SessionState }) => session);
  const [activeSchoolTopic, setActiveSchoolTopic] = useState<number>(0);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const { ref: sectionRef, inView: sectionInView } = useInView({
    threshold: 1,
  });

  const { ref: headerRef, inView: headerInView } = useInView({
    threshold: 1,
  });

  dispatch(setFromExplore(location.pathname.includes('explore')));

  const overviewRef = useRef(null);
  const studyRef = useRef(null);
  const applyRef = useRef(null);
  const careerRef = useRef(null);
  const visaRef = useRef(null);
  const studentLifeRef = useRef(null);
  const faqRef = useRef(null);
  const similarSchoolsRef = useRef(null);

  const navigateTo = (route: string) => {
    navigateTop();
    dispatch(setFromExplore(false));
    navigate(route);
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const element = entry.target;
      let activeTopic = 0;
      if (element.id === 'overview') activeTopic = 0;
      if (element.id === 'study') activeTopic = 1;
      if (element.id === 'apply') activeTopic = 2;
      if (element.id === 'career') activeTopic = 3;
      if (element.id === 'visa') activeTopic = 4;
      if (element.id === 'student-life') activeTopic = 5;
      if (element.id === 'faq') activeTopic = 6;
      if (element.id === 'similar-schools') activeTopic = 7;
      if (entry.isIntersecting && activeTopic != undefined) {
        setActiveSchoolTopic(activeTopic);
      }
    });
  });

  useEffect(() => {
    if (overviewRef.current) observer.observe(overviewRef.current);
    if (studentLifeRef.current) observer.observe(studentLifeRef.current);
    if (studyRef.current) observer.observe(studyRef.current);
    if (similarSchoolsRef.current) observer.observe(similarSchoolsRef.current);
    if (applyRef.current) observer.observe(applyRef.current);
    if (faqRef.current) observer.observe(faqRef.current);
    if (careerRef.current) observer.observe(careerRef.current);
    if (visaRef.current) observer.observe(visaRef.current);
  }, [
    overviewRef.current,
    studyRef.current,
    studentLifeRef.current,
    similarSchoolsRef.current,
    applyRef.current,
    faqRef.current,
    careerRef.current,
    visaRef.current,
    observer,
  ]);

  useEffect(() => {
    if (school?.data.name) document.title = `Unify | ${school?.data.name}`;
  }, [school?.data.name]);

  const showVisaSection = useMemo(
    () =>
      (school?.data.city.country.working_visa_number &&
        school?.data.city.country.working_visa_format) ||
      school?.data.city.country.working_visa_after_study ||
      school?.data.city.country.visa_description,
    [school]
  );

  const showCareerSection = useMemo(
    () =>
      school?.data.school_rankings?.[0] ||
      (school?.data.working_after_months && school?.data.working_after_percentage) ||
      school?.data.alumni_information ||
      (school?.data.companies && school?.data.companies.length > 0) ||
      school?.data.career_service_description,
    [school]
  );

  return (
    <div>
      {comesFromExplore && (
        <Helmet>
          <link
            rel="canonical"
            href={
              schoolSlug?.includes('&spv=1')
                ? `https://unifyapply.com/schools/${schoolSlug.slice(0, -6)}`
                : `https://unifyapply.com/schools/${schoolSlug}`
            }
          />
        </Helmet>
      )}
      {!comesFromExplore && (school?.data.overview || school?.data.meta_description) && (
        <Helmet>
          <meta
            name="description"
            content={
              school?.data.meta_description ? school.data.meta_description : school.data.overview
            }
          />
        </Helmet>
      )}
      {isLoading || isFetching ? (
        <Loader />
      ) : (
        <div className={comesFromExplore ? styles.exploreContainer : ''}>
          <div className={styles.desktopContainer} id="detailContainer">
            {school?.data.photos?.length ? (
              <div
                id="carouselExampleIndicators"
                className={`carousel slide`}
                data-bs-ride="carousel"
              >
                <div className="carousel-indicators">
                  {school.data.photos.length > 1 &&
                    school.data.photos.map((photo: string, index: number) => (
                      <div key={index}>
                        {index === 0 ? (
                          <button
                            type="button"
                            data-bs-target="#carouselExampleIndicators"
                            data-bs-slide-to={`${index}`}
                            className={`active ${styles.buttonDot}`}
                            aria-current="true"
                            aria-label={`Slide ${index}`}
                          ></button>
                        ) : (
                          <button
                            type="button"
                            data-bs-target="#carouselExampleIndicators"
                            data-bs-slide-to={`${index}`}
                            className={`${styles.buttonDot}`}
                            aria-label={`Slide ${index}`}
                          ></button>
                        )}
                      </div>
                    ))}
                </div>
                <div
                  className={`carousel-inner ${
                    comesFromExplore ? styles.carouselContainerExplore : styles.carouselContainer
                  }`}
                >
                  {school.data.photos.length &&
                    school.data.photos.map((photo: string, index: number) => (
                      <div className={`carousel-item ${index === 0 ? 'active' : ''}`} key={index}>
                        <img
                          src={`${photo}`}
                          className={`${styles.photo}`}
                          alt={`Photo ${index + 1} of ${school?.data.name}`}
                        />
                      </div>
                    ))}
                </div>
                {school.data.photos.length > 1 && (
                  <div>
                    <button
                      className={`carousel-control-prev ${styles.carouselButtons}`}
                      type="button"
                      data-bs-target="#carouselExampleIndicators"
                      data-bs-slide="prev"
                    >
                      <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                      <span className="visually-hidden">Previous</span>
                    </button>
                    <button
                      className={`carousel-control-next ${styles.carouselButtons}`}
                      type="button"
                      data-bs-target="#carouselExampleIndicators"
                      data-bs-slide="next"
                    >
                      <span className="carousel-control-next-icon" aria-hidden="true"></span>
                      <span className="visually-hidden">Next</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.placeholderPhoto}>
                <div>
                  <img src={Placeholder} className={`${styles.f}`} alt="placeholder" />
                </div>
              </div>
            )}
            {isMobile && (
              <>
                <div className={styles.headerContainer} ref={headerRef}>
                  <div className="d-flex flex-row align-items-center mt-4 ms-3 gap-2">
                    <img
                      src={school?.data.logo}
                      alt={`Logo of ${school?.data.name}`}
                      className={styles.logoContainer}
                    />
                    <div className={styles.schoolTitle}>
                      <h1 className={styles.titleText}>{school?.data.name}</h1>
                      <div className={styles.countryInfo}>
                        <Text size={14} weight={600}>
                          {school?.data.city.country.name}
                        </Text>
                        <Flag
                          code={school?.data.city.country.code}
                          className={styles.countryFlag}
                          style={{ width: '20px', height: '20px' }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="d-flex flex-row align-items-center gap-4 mt-4 ms-3">
                    {school?.data?.school_rankings?.[0] && (
                      <div className={styles.rankingSection}>
                        <div className={styles.blockNumber}>
                          {school.data.school_rankings[0].score}
                        </div>
                        <div className={styles.rankingInfo}>
                          <p className={`${textStyles['text-primary-12-600']}`}>
                            {school.data.school_rankings[0].ranking.name}
                          </p>
                          <p className={`${textStyles['text-primary-12-600']}`}>
                            {`(${school.data.school_rankings[0].ranking.year})`}
                          </p>
                        </div>
                      </div>
                    )}
                    {school?.data?.school_accreditations?.[0] && (
                      <div className={styles.rankingSection}>
                        <div className={styles.blockNumber}>
                          <img
                            src={school.data.school_accreditations[0].accreditation.logo}
                            alt={`Logo of ${school.data.school_accreditations[0].accreditation.name}`}
                            className={styles.accLogoContainer}
                          />
                        </div>
                        <div className={styles.rankingInfo}>
                          <p className={`${textStyles['text-primary-12-600']}`}>
                            {`${school.data.school_accreditations[0].accreditation.name}-Accredited School`}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  {school?.data?.discounts?.[0] && (
                    <div className="d-flex flex-column mt-4 gap-2">
                      {school.data.discounts.map((discount: Discount) => (
                        <div className={styles.discountContainer} key={discount.id}>
                          <Text size={isMobile ? 11 : 14} weight={500}>
                            {discount.percentage_value === 1 &&
                              discount.kind == 'application_fee' && <del>Application fee £100</del>}
                            &nbsp;
                            {discount.description}
                          </Text>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    className="btn btn-primary btn-lg"
                    onClick={() =>
                      window.open(
                        `https://unifyapply.com/experts/request-meeting?school_id=${school?.data.id}&school_name=${school?.data.name}`
                      )
                    }
                  >
                    Ask expert for help
                  </button>
                </div>
                {!headerInView && (
                  <div className={styles.fixedSchoolTitle}>
                    <div className={styles.schoolInfo}>
                      <img
                        src={school?.data.logo}
                        className={styles.logoContainer}
                        alt={`Logo of ${school?.data.name}`}
                      />
                      <p className={styles.schoolName}>{school?.data.name}</p>
                    </div>
                    <button
                      onClick={() =>
                        window.open(
                          `https://unifyapply.com/experts/request-meeting?school_id=${school?.data.id}&school_name=${school?.data.name}`
                        )
                      }
                    >
                      Ask expert
                    </button>
                  </div>
                )}
              </>
            )}
            {!isMobile ? (
              <div className={comesFromExplore ? '' : 'row'}>
                <div className={comesFromExplore ? 'col-12' : 'col-8'}>
                  <div style={{ paddingTop: '40px' }} ref={sectionRef}></div>
                  <div className={`${styles.section}`}>
                    <img
                      src={school?.data.logo}
                      alt={`Logo of ${school?.data.name}`}
                      className={styles.logoContainerDT}
                    />
                    <div className="d-flex flex-column">
                      {comesFromExplore ? (
                        <h2 className={`${textStyles['text-primary-30-700']}`}>
                          {school?.data.name}
                        </h2>
                      ) : (
                        <h1 className={`${textStyles['text-primary-30-700']}`}>
                          {school?.data.name}
                        </h1>
                      )}
                      <div className={styles.countryInfo}>
                        <Text size={20} weight={600}>
                          {school?.data.city.country.name}
                        </Text>
                        <Flag
                          code={school?.data.city.country.code}
                          className={styles.countryFlag}
                        />
                      </div>
                      {school?.data?.discounts?.[0] && (
                        <div className="d-flex flex-row mt-4 gap-2">
                          {school.data.discounts.map((discount: Discount) => (
                            <div className={styles.discountContainer} key={discount.id}>
                              <Text size={isMobile ? 11 : 14} weight={500}>
                                {discount.percentage_value === 1 &&
                                  discount.kind == 'application_fee' && (
                                    <del>Application fee £100</del>
                                  )}
                                &nbsp;
                                {discount.description}
                              </Text>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className={styles.rankingContainer}>
                      {comesFromExplore && school?.data?.school_rankings?.[0] && (
                        <div className={styles.rankingSection}>
                          <div className={styles.blockNumber}>
                            {school.data.school_rankings[0].score}
                          </div>
                          <div className={styles.rankingInfo}>
                            <p className={`${textStyles['text-primary-14-600']}`}>
                              {school.data.school_rankings[0].ranking.name}
                            </p>
                            <p className={`${textStyles['text-primary-14-600']}`}>
                              {`(${school.data.school_rankings[0].ranking.year})`}
                            </p>
                          </div>
                        </div>
                      )}
                      {comesFromExplore && school?.data?.school_accreditations?.[0] && (
                        <div className={styles.rankingSection}>
                          <div className={styles.blockNumber}>
                            <img
                              src={school.data.school_accreditations[0].accreditation.logo}
                              alt={`Logo of ${school.data.school_accreditations[0].accreditation.name}`}
                              className={styles.accLogoContainer}
                            />
                          </div>
                          <div className={styles.rankingInfo}>
                            <p className={`${textStyles['text-primary-14-600']}`}>
                              {`${school.data.school_accreditations[0].accreditation.name}-Accredited School`}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {comesFromExplore && (
                    <div className={`d-flex flex-row gap-3 ${styles.exploreButtons}`}>
                      <a
                        type="button"
                        className="btn btn-primary btn-lg"
                        href={`https://unifyapply.com/experts/request-meeting?school_id=${school?.data.id}&school_name=${school?.data.name}`}
                      >
                        Ask expert for help
                      </a>
                      <button
                        className={styles.detailButton}
                        onClick={() => {
                          navigateTo(newDetailUrl('schools', school?.data.id, school?.data.name));
                        }}
                      >
                        Read more
                      </button>
                    </div>
                  )}
                  <div className={styles.infoContainer}>
                    <OverviewComponent
                      entity={school?.data}
                      overviewRef={overviewRef}
                      comesFromExplore={comesFromExplore}
                    />
                    <MatchingSection entity={school} comesFromExplore={comesFromExplore} />
                    {school?.data.labels && school?.data.labels.length > 0 && (
                      <TagsSection entity={school?.data} comesFromExplore={comesFromExplore} />
                    )}
                    {!comesFromExplore && school?.data.description && (
                      <div className={styles.sectionContainer}>
                        <Text
                          color="primary"
                          size={isMobile ? 14 : 16}
                          weight={400}
                          inlineStyle={{ textAlign: 'justify', marginTop: '56px' }}
                        >
                          <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                            {school.data.description}
                          </ReactMarkdown>
                        </Text>
                      </div>
                    )}
                    {!comesFromExplore &&
                      (school?.data.international_student_rate ||
                        school?.data.number_of_students ||
                        school?.data.international_student_description) && (
                        <InternationalStudentsComponent
                          school={school?.data}
                          comesFromExplore={comesFromExplore}
                        />
                      )}
                    {!!(school?.data.contact_banner && !comesFromExplore) && (
                      <ContactBanner entity={school} />
                    )}
                    <StudySection
                      school={school}
                      schoolId={Number(schoolId)}
                      logoSchool={String(school?.data.logo)}
                      studyRef={studyRef}
                      comesFromExplore={comesFromExplore}
                    />
                    {!!(school?.data.contact_banner && comesFromExplore) && (
                      <ContactBanner entity={school} />
                    )}
                    {comesFromExplore && (
                      <>
                        <DetailSections entity={school?.data} />
                        <JourneyBanner
                          logged={logged}
                          navigateTo={navigateTo}
                          isMobile={isMobile}
                        />
                      </>
                    )}
                    {!comesFromExplore && (
                      <>
                        <ProfilingBanner />
                        <TeachingSection entity={school} />
                        <ApplySection entity={school} applyRef={applyRef} />
                        {showCareerSection && (
                          <CareerSection entity={school} careerRef={careerRef} />
                        )}
                        {showVisaSection && <VisaSection entity={school} visaRef={visaRef} />}
                        <StudentLife entity={school} studentLifeRef={studentLifeRef} />
                        <FAQSection faqRef={faqRef} entity={school?.data} />
                        <SimilarSchools
                          schoolId={Number(schoolId)}
                          similarSchoolsRef={similarSchoolsRef}
                        />
                        <ProfilingBanner />
                      </>
                    )}
                  </div>
                  <div style={{ paddingTop: 50 }}></div>
                </div>
                {!comesFromExplore && (
                  <div className={`col-4 ${styles.sideColumn}`}>
                    {school?.data?.school_rankings?.[0] && (
                      <div className={styles.rankingSection}>
                        <div className={styles.blockNumber}>
                          {school.data.school_rankings[0].score}
                        </div>
                        <div className={styles.rankingInfo}>
                          <p className={`${textStyles['text-primary-18-600']}`}>
                            {school.data.school_rankings[0].ranking.name}
                          </p>
                          <p className={`${textStyles['text-primary-18-600']}`}>
                            {`(${school.data.school_rankings[0].ranking.year})`}
                          </p>
                        </div>
                      </div>
                    )}
                    {school?.data?.school_accreditations?.[0] && (
                      <div className={styles.rankingSection}>
                        <div className={styles.blockNumber}>
                          <img
                            src={school.data.school_accreditations[0].accreditation.logo}
                            alt={`Logo of ${school.data.school_accreditations[0].accreditation.name}`}
                            className={styles.accLogoContainer}
                          />
                        </div>
                        <div className={styles.rankingInfo}>
                          <p className={`${textStyles['text-primary-18-600']}`}>
                            {`${school.data.school_accreditations[0].accreditation.name}-Accredited School`}
                          </p>
                        </div>
                      </div>
                    )}
                    <TabsComponent school={school || undefined} schoolTopic={activeSchoolTopic} />
                  </div>
                )}
              </div>
            ) : (
              <div>
                <TabsComponent school={school || undefined} schoolTopic={activeSchoolTopic} />
                <OverviewComponent entity={school?.data} overviewRef={overviewRef} />
                <MatchingSection entity={school} />
                {school?.data.labels && school?.data.labels.length > 0 && (
                  <TagsSection entity={school?.data} />
                )}
                {school?.data.description && (
                  <div className={styles.sectionContainer}>
                    <Text
                      color="primary"
                      size={isMobile ? 14 : 16}
                      weight={400}
                      inlineStyle={
                        !isMobile
                          ? { textAlign: 'justify', marginTop: '56px' }
                          : { textAlign: 'justify' }
                      }
                    >
                      <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                        {school.data.description}
                      </ReactMarkdown>
                    </Text>
                  </div>
                )}
                {(school?.data.international_student_rate ||
                  school?.data.number_of_students ||
                  school?.data.international_student_description) && (
                  <InternationalStudentsComponent
                    school={school?.data}
                    comesFromExplore={comesFromExplore}
                  />
                )}
                {!!school?.data.contact_banner && <ContactBanner entity={school} />}
                <StudySection
                  school={school}
                  schoolId={Number(schoolId)}
                  logoSchool={String(school?.data.logo)}
                  studyRef={studyRef}
                />
                <ProfilingBanner />
                <TeachingSection entity={school} />
                <ApplySection entity={school} applyRef={applyRef} />
                {showCareerSection && <CareerSection entity={school} careerRef={careerRef} />}
                {showVisaSection && <VisaSection entity={school} visaRef={visaRef} />}
                <StudentLife entity={school} studentLifeRef={studentLifeRef} />
                <FAQSection faqRef={faqRef} entity={school?.data} />
                <SimilarSchools schoolId={Number(schoolId)} similarSchoolsRef={similarSchoolsRef} />
                <ProfilingBanner />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolDetail;
